// Ground heights for a region. The stored grid has one sample every `step`
// metres; the game's ground mesh has a vertex every MESH_STEP metres and each
// square is split along the same diagonal used here, so heightAt() returns
// exactly the surface that is drawn.
export const MESH_STEP = 16;

export class Heightmap {
  // meta: { x0, z0, step, w, h }; data: Uint16Array of decimetres
  constructor(meta, data) {
    this.x0 = meta.x0;
    this.z0 = meta.z0;
    this.step = meta.step;
    this.w = meta.w;
    this.h = meta.h;
    this.data = data;
  }

  // Bilinear height of the stored grid at a world position.
  gridHeight(x, z) {
    let fx = (x - this.x0) / this.step;
    let fz = (z - this.z0) / this.step;
    if (fx < 0) fx = 0;
    else if (fx > this.w - 1.001) fx = this.w - 1.001;
    if (fz < 0) fz = 0;
    else if (fz > this.h - 1.001) fz = this.h - 1.001;
    const i = fx | 0;
    const j = fz | 0;
    const ax = fx - i;
    const az = fz - j;
    const d = this.data;
    const k = j * this.w + i;
    const a = d[k];
    const b = d[k + 1];
    const c = d[k + this.w];
    const e = d[k + this.w + 1];
    return (a + (b - a) * ax + (c - a) * az + (a - b - c + e) * ax * az) / 10;
  }

  // Height of the drawn ground at a world position.
  heightAt(x, z) {
    const s = MESH_STEP;
    const ix = Math.floor(x / s);
    const iz = Math.floor(z / s);
    const fx = x / s - ix;
    const fz = z / s - iz;
    const x0 = ix * s;
    const z0 = iz * s;
    const h00 = this.gridHeight(x0, z0);
    const h11 = this.gridHeight(x0 + s, z0 + s);
    if (fx > fz) {
      const h10 = this.gridHeight(x0 + s, z0);
      return h00 + (h10 - h00) * fx + (h11 - h10) * fz;
    }
    const h01 = this.gridHeight(x0, z0 + s);
    return h00 + (h01 - h00) * fz + (h11 - h01) * fx;
  }

  // Unit normal of the ground, as [nx, ny, nz].
  normalAt(x, z) {
    const e = 2;
    const dx = this.heightAt(x + e, z) - this.heightAt(x - e, z);
    const dz = this.heightAt(x, z + e) - this.heightAt(x, z - e);
    const nx = -dx / (2 * e);
    const nz = -dz / (2 * e);
    const len = Math.hypot(nx, 1, nz);
    return [nx / len, 1 / len, nz / len];
  }
}
